<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";

type Props = {
  onClose: () => void;
  onSave: (value: string) => void;
  isLoading: boolean;
  isFailed: boolean;
};

const props = defineProps<Props>();

const feedback = ref("");
const dialog = ref<HTMLDialogElement | null>(null);
const closeBtn = ref<HTMLElement | null>(null);

const onSubmit = (e: Event): void => {
  e.preventDefault();
  props.onSave(feedback.value);
};

const onClickBackdrop = (e: Event): void => {
  if (e.target == dialog.value) {
    closeBtn.value?.focus();
  }
};

onMounted(() => {
  dialog.value?.addEventListener("click", onClickBackdrop);
  dialog.value?.showModal();
});

onUnmounted(() => {
  dialog.value?.removeEventListener("click", onClickBackdrop);
});
</script>

<template>
  <dialog id="feedback-form" ref="dialog">
    <slot name="content">
      <header>
        <h4>Feedback</h4>
      </header>
      <form @submit="onSubmit">
        <textarea v-model="feedback" placeholder="We want to hear from you!" required autofocus />
        <div v-if="props.isFailed" class="alert alert-danger">
          Something went wrong while delivering your feedback. Please try again.
        </div>
        <div class="button-container">
          <button ref="closeBtn" class="btn btn-cancel" :disabled="isLoading" @click="props.onClose">Cancel</button>
          <button class="btn btn-primary" :disabled="isLoading" type="submit">Submit</button>
        </div>
      </form>
    </slot>
  </dialog>
</template>

<style scoped>
h4 {
  text-transform: uppercase;
  font-size: 1.25rem;
}
::backdrop {
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75));
  opacity: 0.75;
}

dialog {
  padding: 0;
  border: none;
  border-radius: 0.5rem;
  background-color: white;
  max-height: 60vh;
  width: clamp(20rem, 50vw, 40rem);
  scrollbar-width: thin;
}

form > textarea {
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  resize: vertical;
  min-height: clamp(4rem, 50vh, 10rem);
  max-height: clamp(4rem, 50vh, 15rem);
}

form > textarea::placeholder {
  color: #96a0aa;
}

form {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: 1.5rem;
  gap: 1rem;
}

header {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #85140b;
  color: white;
  padding: 0.5rem 0.5rem 0;
}

.button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

button {
  padding: 0.4rem clamp(1rem, 5vw, 4rem);
  text-transform: uppercase;
  font-weight: bold;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
    border-color: var(--bs-primary);
  }
}

.btn-cancel {
  background-color: #fff;
  color: #797979;
  border: 1px solid #ccc;
}
</style>
